import React from 'react';
import styles from './TcsoDualFuelResultsScreen.module.css';
import buttonStyles from './TcsoInventoryCheckScreen.module.css';
import inputStyles from './TcsoDualFuelMeterDetailsScreen.module.css';
import BackButton from '../common/button/BackButton';
import SuccessIcon from '../common/icons/SuccessIcon';
import ErrorIcon from '../common/icons/ErrorIcon';
import { DualFuelCommsCheckResult } from '../../types/comms-check';
import {
  ACTION_TEXT,
  determineDualFuelCommsOutcome,
  FuelCommsOutcome,
  OTHER_DEVICES_TEXT,
  STATUS_TEXT,
} from '../commsCheck/dualFuelCommsCheckOutcome';
import { AddressSection, DeviceListSection, ReadingsSection } from './TcsoDualFuelResultSections';

interface TcsoDualFuelErrorScreenProps {
  result: DualFuelCommsCheckResult;
  onTryAgain: () => void;
  onStartChat: () => void;
  onPrevious: () => void;
  onBackToStart: () => void;
}

interface FuelBannerProps {
  outcome: FuelCommsOutcome;
  onTryAgain: () => void;
}

const FuelBanner: React.FC<FuelBannerProps> = ({ outcome, onTryAgain }) => {
  const statusText = STATUS_TEXT[outcome.status](outcome.fuel);

  if (outcome.action === null) {
    return (
      <div className={styles.successBanner}>
        <SuccessIcon className={styles.bannerIcon} />
        <div className={styles.successBannerText}>{statusText}</div>
      </div>
    );
  }

  return (
    <div className={styles.errorBanner}>
      <ErrorIcon className={styles.bannerIcon} />
      <div className={styles.errorBannerBody}>
        <p className={styles.errorBannerText}>
          <span className={styles.bannerLabel}>Status: </span>
          {statusText}
        </p>
        <p className={styles.errorBannerText}>
          <span className={styles.bannerLabel}>Action to take: </span>
          {ACTION_TEXT[outcome.action]}
        </p>
        <div className={styles.tryAgainRow}>
          <button type="button" className={styles.tryAgainButton} onClick={onTryAgain}>
            Try again
          </button>
        </div>
      </div>
    </div>
  );
};

export default function TcsoDualFuelErrorScreen({
  result,
  onTryAgain,
  onStartChat,
  onPrevious,
  onBackToStart,
}: TcsoDualFuelErrorScreenProps) {
  const { elec, gas, hasOtherDevices } = determineDualFuelCommsOutcome(result);
  const showAddress = result.xrinv.state === 'SUCCESS' && !!result.xrinv.address;
  const showDevices = result.xrinv.state === 'SUCCESS' && result.xrinv.data.length > 0;
  const showElecReadings = result.elecXritm.state === 'SUCCESS';
  const showGasReadings = result.gasXritm.state === 'SUCCESS';
  const showPanel = showAddress || showDevices || showElecReadings || showGasReadings;

  return (
    <div className={`commsCheckDecommFlexContainer ${inputStyles.screenContainer}`}>
      <div className={'commsCheckDecommContent'}>
        <div className={'m-t-8'}>
          <BackButton onClick={onBackToStart} />
          <div className={'bodyTitleText m-t-22'}>Comms Check Results</div>
          <FuelBanner outcome={elec} onTryAgain={onTryAgain} />
          <FuelBanner outcome={gas} onTryAgain={onTryAgain} />
          {hasOtherDevices && (
            <div className={styles.errorBanner}>
              <ErrorIcon className={styles.bannerIcon} />
              <div className={styles.errorBannerBody}>
                <p className={styles.errorBannerText}>{OTHER_DEVICES_TEXT}</p>
              </div>
            </div>
          )}
          {showPanel && (
            <div className={styles.resultsPanel}>
              {showAddress && <AddressSection address={result.xrinv.address as string} />}
              {showElecReadings && <ReadingsSection heading="Reading for Elec MSN" xritm={result.elecXritm} />}
              {showGasReadings && <ReadingsSection heading="Reading for Gas MSN" xritm={result.gasXritm} />}
              {showDevices && <DeviceListSection devices={result.xrinv.data} />}
            </div>
          )}
        </div>
      </div>
      <div className={`tcsoFooter ${inputStyles.footer}`}>
        <button
          className={`${buttonStyles.tcsoTimeoutButton} ${buttonStyles.bgcGreen} ${buttonStyles.mGB8}`}
          onClick={onStartChat}
        >
          Start Chat
        </button>
        <button className={`${buttonStyles.tcsoTimeoutButton} ${buttonStyles.bgcDefault}`} onClick={onPrevious}>
          Previous
        </button>
      </div>
    </div>
  );
}
